import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useAuth } from '../context/AuthContext';
import { StickyNote, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const ItemNotesCard = ({ itemId }) => {
  const { api, user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchNotes = async () => {
    try {
      const { data } = await api().get(`/items/${itemId}/notes`);
      setNotes(data || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (itemId) fetchNotes(); /* eslint-disable-next-line */ }, [itemId]);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await api().post(`/items/${itemId}/notes`, { content: trimmed });
      setContent('');
      toast.success('Note added');
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (note) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api().delete(`/items/${itemId}/notes/${note.id}`);
      toast.success('Note deleted');
      setNotes(prev => prev.filter(n => n.id !== note.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete note');
    }
  };

  const canDelete = (note) => user && (user.role === 'Admin' || user.id === note.author_id);

  return (
    <Card className="industrial-card" data-testid="item-notes-card">
      <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <StickyNote className="w-4 h-4" /> Notes
        </h2>
        <span className="text-xs text-slate-500 mono">{notes.length}</span>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <form onSubmit={submit} className="space-y-2" data-testid="item-note-form">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Add a note about usage, observations, vendor feedback…"
            className="rounded-sm"
            rows={3}
            data-testid="item-note-input"
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={submitting || !content.trim()}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              data-testid="item-note-submit-btn"
            >
              <Send className="w-3 h-3 mr-2" /> {submitting ? 'Saving…' : 'Add Note'}
            </Button>
          </div>
        </form>

        {loading ? (
          <div className="text-sm text-slate-500 text-center py-4">Loading…</div>
        ) : notes.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-4" data-testid="item-notes-empty">
            No notes yet. Use the field above to leave the first one.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {notes.map(n => (
              <li key={n.id} className="py-3 flex items-start gap-3" data-testid={`item-note-${n.id}`}>
                <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-xs font-bold shrink-0">
                  {(n.author_name || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-slate-800">{n.author_name}</span>
                    <span className="text-xs text-slate-400">
                      {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                    </span>
                  </div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap mt-0.5">{n.content}</div>
                </div>
                {canDelete(n) && (
                  <Button
                    variant="ghost" size="sm"
                    className="text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                    onClick={() => handleDelete(n)}
                    data-testid={`item-note-delete-${n.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default ItemNotesCard;
